import { Button } from '@/components/shadcn/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { cn } from '@/lib/utils'
import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IconCheckMarkOutline } from '@icons/IconCheckMarkOutline'
import { IconCloseOutline } from '@icons/IconCloseOutline'
import { IconDownChevronOutline } from '@icons/IconDownChevronOutline'
import { useVirtualizer } from '@tanstack/react-virtual'
import { BaseFieldProps } from '../types'
import clsx from 'clsx'

interface Option {
  label: string
  value: string
}

interface SingleLevelComboboxProps {
  options: Option[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  variant: BaseFieldProps['variant']
  // When rendering in a row, apply inward notches using clip-paths
  showLeftNotch?: boolean
  showRightNotch?: boolean
}

export function SingleLevelCombobox({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select option',
  disabled = false,
  variant = 'column',
  showLeftNotch = false,
  showRightNotch = false,
}: SingleLevelComboboxProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const hasScrolledRef = useRef(false)

  // Ensure options is always an array
  const safeOptions = useMemo(() => (Array.isArray(options) ? options : []), [options])

  // Find the selected option label
  const selectedOption = useMemo(() => safeOptions.find((option) => option.value === value), [safeOptions, value])

  // Filter options based on search query
  const filteredOptions = useMemo(
    () =>
      searchQuery
        ? safeOptions.filter((option) => option.label.toLowerCase().includes(searchQuery.toLowerCase()))
        : safeOptions,
    [safeOptions, searchQuery]
  )

  // Set up virtualization
  const rowVirtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 36, // Approximate height of each option item
    overscan: 10,
  })

  // Reset search and scroll state when dropdown closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('')
      hasScrolledRef.current = false
    }
  }, [open])

  // Handle scrolling to selected item
  const scrollToSelectedItem = useCallback(() => {
    if (!open || !listRef.current || hasScrolledRef.current || !value) return

    const selectedIndex = filteredOptions.findIndex((option) => option.value === value)
    if (selectedIndex === -1) return

    // Use a small timeout to ensure the scroll container is ready
    const timer = setTimeout(() => {
      if (listRef.current) {
        rowVirtualizer.scrollToIndex(selectedIndex, { align: 'center' })
        hasScrolledRef.current = true
      }
    }, 100)

    return () => clearTimeout(timer)
  }, [open, value, filteredOptions, rowVirtualizer])

  // Apply the scroll effect
  useEffect(() => {
    return scrollToSelectedItem()
  }, [scrollToSelectedItem])

  const handleSelect = useCallback(
    (currentValue: string) => {
      // If the same value is selected, clear the selection
      if (currentValue === value) {
        onChange('')
      } else {
        onChange(currentValue)
      }
      setOpen(false)
    },
    [onChange, value]
  )

  const handleClear = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      onChange('')
    },
    [onChange]
  )

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    hasScrolledRef.current = false // Reset scroll state when search changes
  }, [])

  // Render a simple list of options for debugging
  const renderSimpleOptionsList = () => {
    return (
      <div className="max-h-[300px] overflow-auto p-2">
        {filteredOptions.length === 0 ? (
          <div className="py-6 text-center text-sm">No results found.</div>
        ) : (
          filteredOptions.map((option) => (
            <div
              key={option.value}
              className={cn(
                'flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-muted',
                value === option.value && 'bg-muted'
              )}
              onClick={() => handleSelect(option.value)}
            >
              <IconCheckMarkOutline
                className={cn('mr-2 h-4 w-4 shrink-0', value === option.value ? 'opacity-100' : 'opacity-0')}
              />
              {option.label}
            </div>
          ))
        )}
      </div>
    )
  }

  return (
    <div className="relative w-full">
      <div className="flex w-full items-center">
        <div className="relative flex-1 overflow-visible">
          <Popover open={open} onOpenChange={setOpen}>
            <div className="group">
              <PopoverTrigger asChild>
                <Button
                  variant="input"
                  role="combobox"
                  aria-expanded={open}
                  className={cn('relative w-full items-center justify-between', !value && 'text-muted-foreground', {
                    'h-6 min-h-0 min-w-48 px-2 py-0': variant === 'row',
                    'rounded-l-none border-l-0 pl-3': variant === 'row' && showLeftNotch,
                    'rounded-r-none border-r-0': variant === 'row' && showRightNotch,
                  })}
                  // style={
                  //   variant === 'row'
                  //     ? // ? showLeftNotch
                  //       showRightNotch
                  //       ? { clipPath: 'polygon(12px 0, 100% 0, 100% 100%, 12px 100%, 0 50%)' }
                  //       : undefined
                  //     : undefined
                  // }
                  disabled={disabled}
                >
                  <span className="flex min-w-0 flex-1 items-center truncate">{value && selectedOption ? selectedOption.label : placeholder}</span>
                  <span className={cn('ml-2 inline-flex items-center gap-1', variant === 'row' && 'gap-1 pr-1')}>
                    <IconDownChevronOutline className="h-3 w-3 shrink-0 opacity-50" />
                    {variant === 'row' && !!value && (
                      <span
                        role="button"
                        tabIndex={0}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                        onClick={(e) => handleClear(e)}
                        aria-label="Clear selection"
                        className={clsx(
                          'z-20 flex h-[1.125rem] w-[1.125rem] items-center justify-center rounded-full text-muted-foreground hover:border-brand-hover hover:text-brand-hover',
                          variant === 'row' && 'absolute -right-2 -top-2 border bg-background'
                        )}
                      >
                        <IconCloseOutline size={10} className="opacity-60" />
                      </span>
                    )}
                  </span>
                </Button>
              </PopoverTrigger>
              {variant === 'row' && showRightNotch && (
                <span
                  aria-hidden
                  className="pointer-events-none absolute right-[-8px] top-1/2 z-10 h-4 w-4 -translate-x-[10%] -translate-y-1/2 rotate-45 border-r border-t border-border bg-background"
                />
              )}
            </div>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
              <div className="flex flex-col">
                <div className="flex items-center border-b px-3">
                  <input
                    ref={inputRef}
                    className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    autoFocus
                  />
                </div>

                {/* Use simple list rendering instead of virtualization for now */}
                {renderSimpleOptionsList()}

                {/* Commented out virtualization for debugging
                {filteredOptions.length === 0 ? (
                  <div className="py-6 text-center text-sm">No results found.</div>
                ) : (
                  <div 
                    ref={listRef} 
                    className="max-h-[300px] overflow-auto"
                  >
                    <div
                      style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        width: '100%',
                        position: 'relative',
                      }}
                    >
                      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        const option = filteredOptions[virtualRow.index]
                        if (!option) return null

                        return (
                          <div
                            key={option.value}
                            data-index={virtualRow.index}
                            className={cn(
                              'absolute left-0 top-0 w-full',
                              'flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-grey-lightest',
                              value === option.value && 'bg-grey-lightest'
                            )}
                            style={{
                              height: `${virtualRow.size}px`,
                              transform: `translateY(${virtualRow.start}px)`,
                            }}
                            onClick={() => handleSelect(option.value)}
                          >
                            <IconCheckMarkOutline
                              className={cn('mr-2 h-4 w-4', value === option.value ? 'opacity-100' : 'opacity-0')}
                            />
                            {option.label}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
                */}
              </div>
            </PopoverContent>
          </Popover>
        </div>
        {/* Clear button next to trigger for column variant only */}
        {variant !== 'row' && value && (
          <Button
            variant="ghost"
            size="icon"
            className={cn('ml-1 h-9 w-9 p-0')}
            onClick={handleClear}
            aria-label="Clear selection"
            disabled={disabled}
          >
            <IconCloseOutline size={12} className="shrink-0 opacity-50 hover:opacity-100" />
          </Button>
        )}
      </div>
    </div>
  )
}
